import type { ModuleId, PermissionLevel, RequiredLevel } from '@qlk/shared';
import type { ComponentType, LazyExoticComponent } from 'react';

import { coQuyen } from './auth.js';

/**
 * Điểm mở rộng của trang **Cài đặt** (`/cai-dat`).
 *
 * Module nào có một trang cấu hình thì tạo file
 * `apps/web/src/modules/<id>/caiDatTabs.tsx` export mảng `TabCaiDat`
 * (`export default [...]` hoặc `export const caiDatTabs = [...]`). Khung nạp
 * tự động, nên thêm một mục cấu hình **không** phải sửa code của `team` hay của
 * khung — đúng như `channelTabs` của trang chi tiết kênh.
 *
 * ```tsx
 * const caiDatTabs: TabCaiDat[] = [
 *   {
 *     id: 'ket-noi-api',
 *     label: 'Kết nối API',
 *     order: 20,
 *     permission: { module: 'sync', level: 'view' },
 *     component: lazy(() => import('./components/TabKetNoiApi.js')),
 *   },
 * ];
 * export default caiDatTabs;
 * ```
 *
 * Nút hành động của một mục nằm trong **thân mục** (thanh công cụ riêng), không
 * ở `PageHeader`: khung trang không được biết việc của module nào, mà mọi nút
 * cấu hình đều cần trạng thái bên trong mục.
 */
export interface TabCaiDat {
  /** Id — cũng là giá trị của `?tab=` trên URL, nên **không đổi** về sau. */
  id: string;
  label: string;
  /** Một dòng phụ dưới tiêu đề trang khi mục này đang mở. */
  phu?: string;
  /** Đoạn dài hiện trong tooltip (?) cạnh tiêu đề. */
  giaiThich?: string;
  /** Nhỏ hơn đứng trước. */
  order: number;
  /** Quyền tối thiểu để thấy mục. Thiếu quyền thì mục bị ẩn hẳn. */
  permission: { module: ModuleId; level: RequiredLevel };
  /** Luôn lazy-load: chỉ tải bundle khi người dùng mở mục. */
  component: LazyExoticComponent<ComponentType>;
}

type ModuleTab = { default?: TabCaiDat[]; caiDatTabs?: TabCaiDat[] };

const DA_NAP = import.meta.glob<ModuleTab>('../modules/*/caiDatTabs.{ts,tsx}', { eager: true });

/** Mọi mục đã đăng ký, đã sắp theo `order`. */
export function tatCaTabCaiDat(): TabCaiDat[] {
  return Object.values(DA_NAP)
    .flatMap((m) => m.default ?? m.caiDatTabs ?? [])
    .sort((a, b) => a.order - b.order);
}

/** Mục mà người dùng hiện tại được xem. */
export function tabCaiDatTheoQuyen(
  permissions: Record<string, PermissionLevel> | undefined,
): TabCaiDat[] {
  return tatCaTabCaiDat().filter((t) =>
    coQuyen(permissions, t.permission.module, t.permission.level),
  );
}
